import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { TableWithPagination } from "@components/Tables";
import ConfirmActionModal from "@components/UI/ConfirmActionModal";
import { useTheme } from "@hooks/useTheme";
import useEscapeKey from "@hooks/useEscapeKey";
import { fetchVexImportSources, importVexFromPreviousVersion } from "@apis/vex";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showInfoToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { VEX_VERIFICATION_SEQ_FIELD } from "@constants/vex";
import { PermissionContext } from "@contexts/PermissionContext";
import { formatDateTime } from "@utils/common";

/**
 * Pick a previous verification (same scan tool) and POST /vex/import into the current one.
 * Source list is served by GET /vex/import-sources/{scanToolCd} (BE filters DONE + previous only).
 */
const VexImportFromPreviousVersionModal = ({
  show,
  onClose,
  projectSeq,
  scanToolCd,
  targetVerificationSeq,
  projectManagerId,
  "data-testid": dataTestId = "vex-import-previous-version-modal",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const queryClient = useQueryClient();
  const { canEditVex } = useContext(PermissionContext) || {};

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedSeq, setSelectedSeq] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const seqField = VEX_VERIFICATION_SEQ_FIELD[scanToolCd] || "verificationSeq";

  useEscapeKey(onClose, show && !confirmOpen);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show]);

  const listQuery = useQuery({
    queryKey: [queryKeys.vexImportSources, scanToolCd, projectSeq, targetVerificationSeq],
    queryFn: () =>
      fetchVexImportSources({
        scanToolCd,
        projectSeq,
        targetVerificationSeq,
      }),
    enabled: show && Boolean(projectSeq && scanToolCd && targetVerificationSeq != null),
  });

  const allItems = useMemo(() => (Array.isArray(listQuery.data) ? listQuery.data : []), [listQuery.data]);
  const loading = listQuery.isFetching;

  useEffect(() => {
    if (listQuery.isError) {
      showErrorToast(t("status.vex-import-list-error"));
    }
  }, [listQuery.isError, t]);

  useEffect(() => {
    if (!show) return;
    setCurrentPage(1);
    setItemsPerPage(5);
    setSelectedSeq(null);
    setConfirmOpen(false);
  }, [show, targetVerificationSeq]);

  const totalData = allItems.length;
  const totalPages = Math.max(1, Math.ceil(totalData / itemsPerPage) || 1);
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return allItems.slice(start, start + itemsPerPage);
  }, [allItems, currentPage, itemsPerPage]);

  const selectedItem = useMemo(
    () => allItems.find((row) => String(row?.[seqField]) === String(selectedSeq)) || null,
    [allItems, selectedSeq, seqField],
  );

  const importMutation = useMutation({
    mutationFn: () =>
      importVexFromPreviousVersion({
        scanToolCd,
        targetVerificationSeq,
        sourceVerificationSeq: selectedSeq,
      }),
    onSuccess: async (data) => {
      const imported = data?.imported ?? 0;
      if (imported > 0) {
        showSuccessToast(
          t("status.vex-import-success", { imported }),
        );
      } else {
        showInfoToast(t("status.vex-import-empty"), "");
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.vexList] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.vexProgress] }),
      ]);
      setConfirmOpen(false);
      onClose();
    },
    onError: (error) => {
      const message = error?.response?.data?.message || t("status.vex-import-error");
      showErrorToast(message);
    },
  });

  const handleRadioChange = (selected) => {
    if (selected?.length) {
      setSelectedSeq(selected[0]);
    } else {
      setSelectedSeq(null);
    }
  };

  const columns = ["verificationVersion", "realFileName", "verificationStatusCd", "requesterName", "requestedAt"];

  const columnConfig = {
    verificationVersion: {
      title: t("common.version"),
      customRender: (_v, row) => (
        <span className={`text-sm font-medium ${colors.textPrimary}`}>{row.verificationVersion || "---"}</span>
      ),
      style: { width: "12%" },
    },
    realFileName: {
      title: t("project.sbom.verif-file-name"),
      customRender: (_v, row) => (
        <span className={`text-sm font-medium ${colors.textPrimary}`}>{row.realFileName || "---"}</span>
      ),
      style: { width: "28%" },
    },
    verificationStatusCd: {
      title: t("project.sbom.verif-status"),
      customRender: (_v, row) => {
        const statusText = {
          ON_ANALYSIS: t("status.analyzing"),
          ANALYSIS_CHECK: t("status.analysisCheck"),
          REQUEST: t("status.request"),
          ON_VERIFICATION: t("status.onVerification"),
          ON_REVIEW: t("status.onReview"),
          STANDBY_FOLLOWUP: t("status.standbyFollowup"),
          FOLLOWUP_CHECK: t("status.followupCheck"),
          DONE: t("status.done"),
          REVERIFICATION: t("status.reverification"),
          ERROR: t("status.error"),
        };
        const statusColorDot = {
          ON_ANALYSIS: colors.bgStatusAnalysis,
          ANALYSIS_CHECK: colors.bgStatusAnalysis,
          REQUEST: colors.bgStatusRequest,
          ON_VERIFICATION: colors.bgStatusVerification,
          ON_REVIEW: colors.bgStatusReview,
          STANDBY_FOLLOWUP: colors.bgStatusFollowup,
          FOLLOWUP_CHECK: colors.bgStatusFollowup,
          DONE: colors.bgStatusSuccess,
          REVERIFICATION: colors.bgStatusReverification,
          ERROR: colors.bgStatusAnalysis,
        };
        let displayText = statusText[row.verificationStatusCd];
        if (row.verificationStatusCd === "ON_VERIFICATION" && row.verificationStatusCdName) {
          displayText = row.verificationStatusCdName;
        }
        return (
          <span className={`inline-flex items-center text-sm font-semibold ${colors.textPrimary}`}>
            <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[row.verificationStatusCd] || colors.bgStatusTagNeutral}`} />
            {displayText || row.verificationStatusCd || "---"}
          </span>
        );
      },
      style: { width: "22%" },
    },
    requesterName: {
      title: t("common.requester"),
      customRender: (_v, row) => (
        <span className={`text-sm font-medium ${colors.textPrimary}`}>{row.requesterName || "---"}</span>
      ),
      style: { width: "18%" },
    },
    requestedAt: {
      title: t("common.requested-at"),
      customRender: (_v, row) => (
        <span className={`text-sm font-medium ${colors.textPrimary}`}>
          {row.requestedAt ? formatDateTime(row.requestedAt) : "---"}
        </span>
      ),
      style: { width: "20%" },
    },
  };

  if (!show) return null;

  const canImport = Boolean(canEditVex?.(projectManagerId) && selectedSeq);

  return (
    <>
      <div
        className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start xl:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`}
        data-testid={`${dataTestId}-overlay`}
      >
        <div
          className={`${colors.bgModal} w-[1100px] min-w-[1000px] max-h-[90vh] flex-shrink-0 overflow-auto rounded-lg shadow-xl xl:overflow-y-hidden flex flex-col`}
          data-testid={dataTestId}
        >
          <div className="px-6 py-4 flex-shrink-0" data-testid={`${dataTestId}-header`}>
            <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
              {t("status.vex-import-modal-title")}
            </h2>
            <p className={`${colors.textSecondary} text-sm mt-1`}>
              {t("status.vex-import-modal-instruction")}
            </p>
          </div>

          <div className="px-6 pb-4 flex-1 min-h-0 flex flex-col" data-testid={`${dataTestId}-content`}>
            <TableWithPagination
              columns={columns}
              data={pageData}
              colSize={12}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              loading={loading}
              totalPages={totalPages}
              totalData={totalData}
              columnConfig={columnConfig}
              onRowClick={(row) => setSelectedSeq(row?.[seqField])}
              maxRowsPerPage={itemsPerPage}
              onItemsPerPageChange={(n) => {
                setItemsPerPage(n);
                setCurrentPage(1);
              }}
              showPagination
              tableStyle="default"
              rowKey={seqField}
              hasRadioButton
              selectedRows={selectedSeq != null ? [selectedSeq] : []}
              onCheckboxChange={handleRadioChange}
              dataTestId={`${dataTestId}-table`}
            />
          </div>

          <div className="px-6 py-4 flex-shrink-0" data-testid={`${dataTestId}-footer`}>
            <div className="flex justify-end gap-4">
              <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose}>
                {t("common.cancel-button")}
              </OutlineButton>
              <PrimaryButton
                data-testid={`${dataTestId}-import-button`}
                onClick={() => setConfirmOpen(true)}
                disabled={!canImport}
              >
                {t("status.vex-import-button")}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>

      <ConfirmActionModal
        isOpen={confirmOpen}
        onClose={() => !importMutation.isPending && setConfirmOpen(false)}
        onConfirm={() => importMutation.mutate()}
        loading={importMutation.isPending}
        title={t("status.vex-import-confirm-title")}
        description={t("status.vex-import-confirm-description", {
          version: selectedItem?.verificationVersion || selectedSeq,
          fileName: selectedItem?.realFileName || "",
        })}
        confirmText={t("status.vex-import-button")}
        cancelText={t("common.cancel-button")}
        data-testid={`${dataTestId}-confirm`}
      />
    </>
  );
};

export default VexImportFromPreviousVersionModal;
